/*
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import { jsPDF } from 'jspdf';
import { autoTable } from 'jspdf-autotable';

/**
 * A directive for viewing connection history records.
 */
angular.module('settings').directive('guacSettingsConnectionHistory', [function guacSettingsConnectionHistory() {
        
    return {
        // Element only
        restrict: 'E',
        replace: true,

        scope: {
        },

        templateUrl: 'app/settings/templates/settingsConnectionHistory.html',
        controller: ['$scope', '$injector', function settingsConnectionHistoryController($scope, $injector) {
                
            // Get required types
            var ConnectionHistoryEntryWrapper = $injector.get('ConnectionHistoryEntryWrapper');
            var FilterToken                   = $injector.get('FilterToken');
            var SortOrder                     = $injector.get('SortOrder');

            // Get required services
            var $filter        = $injector.get('$filter');
            var $routeParams   = $injector.get('$routeParams');
            var $translate     = $injector.get('$translate');
            var csvService     = $injector.get('csvService');
            var historyService = $injector.get('historyService');
            var requestService = $injector.get('requestService');

            /**
             * The identifier of the currently-selected data source.
             *
             * @type String
             */
            $scope.dataSource = $routeParams.dataSource;

            /**
             * All wrapped matching connection history entries, or null if these
             * entries have not yet been retrieved.
             *
             * @type ConnectionHistoryEntryWrapper[]
             */
            $scope.historyEntryWrappers = null;

            /**
             * The search terms to use when filtering the history records.
             *
             * @type String
             */
            $scope.searchString = '';

            /**
             * The date format for use for start/end dates.
             *
             * @type String
             */
            $scope.dateFormat = null;

            /**
             * Whether the per-column filter row is currently visible and in
             * effect. Toggled via the funnel/filter icon in the toolbar.
             * While false, the column filters below are completely ignored,
             * regardless of whatever values they may already contain.
             *
             * @type {!boolean}
             */
            $scope.columnFiltersEnabled = false;

            /**
             * Per-column filter values, applied client-side against the
             * history records already retrieved via search(), in addition
             * to (and independently of) searchString. Text columns
             * (username, connectionName, remoteHost) accept a
             * comma-separated list of terms; a record matches if the
             * corresponding field contains ANY of the given terms. The two
             * date columns (start/end) each accept an inclusive From/To
             * range; either bound may be left unset.
             *
             * @type {!Object}
             */
            $scope.columnFilters = {

                username       : '',
                connectionName : '',
                remoteHost     : '',

                /**
                 * Only history records having a start date/time on or after
                 * this point in time will match, if set.
                 *
                 * @type {Date}
                 */
                startDateFrom : null,

                /**
                 * Only history records having a start date/time on or
                 * before this point in time will match, if set.
                 *
                 * @type {Date}
                 */
                startDateTo : null,

                /**
                 * Only history records having an end date/time on or after
                 * this point in time will match, if set.
                 *
                 * @type {Date}
                 */
                endDateFrom : null,

                /**
                 * Only history records having an end date/time on or
                 * before this point in time will match, if set.
                 *
                 * @type {Date}
                 */
                endDateTo : null

            };

            /**
             * SortOrder instance which stores the sort order of the history
             * records.
             *
             * @type SortOrder
             */
            $scope.order = new SortOrder([
                '-entry.startDate',
                '-duration',
                'entry.username',
                'entry.connectionName',
                'entry.remoteHost'
            ]);

            /**
             * The names of sortable properties supported by the REST API that
             * correspond to the properties that may be stored within
             * $scope.order.
             *
             * @type {!Object.<string, string>}
             */
            const apiSortProperties = {
                 'entry.startDate' :  'startDate',
                '-entry.startDate' : '-startDate'
            };

            /**
             * Converts the given sort predicate to a correponding array of
             * sortable properties supported by the REST API. Any properties
             * within the predicate that are not supported will be dropped.
             *
             * @param {!string[]} predicate
             *     The sort predicate to convert, as exposed by the predicate
             *     property of SortOrder.
             *
             * @returns {!string[]}
             *     A corresponding array of sortable properties, omitting any
             *     properties not supported by the REST API.
             */
            var toAPISortPredicate = function toAPISortPredicate(predicate) {
                return predicate
                        .map((name) => apiSortProperties[name])
                        .filter((name) => !!name);
            };

            // Get session date format
            $translate('SETTINGS_CONNECTION_HISTORY.FORMAT_DATE')
            .then(function dateFormatReceived(retrievedDateFormat) {

                // Store received date format
                $scope.dateFormat = retrievedDateFormat;

            }, angular.noop);
            
            /**
             * Returns true if the connection history records have been loaded,
             * indicating that information needed to render the page is fully 
             * loaded.
             * 
             * @returns {Boolean} 
             *     true if the history records have been loaded, false
             *     otherwise.
             * 
             */
            $scope.isLoaded = function isLoaded() {
                return $scope.historyEntryWrappers !== null
                    && $scope.dateFormat           !== null;
            };

            /**
             * Returns whether the search has completed but contains no history
             * records. This function will return false if there are history
             * records in the results OR if the search has not yet completed.
             *
             * @returns {Boolean}
             *     true if the search results have been loaded but no history
             *     records are present, false otherwise.
             */
            $scope.isHistoryEmpty = function isHistoryEmpty() {
                return $scope.isLoaded()
                    && $filter('filter')($scope.historyEntryWrappers, $scope.matchesColumnFilters).length === 0;
            };

            /**
             * Returns whether the given field contains at least one of the
             * comma-separated terms present within the given filter value.
             * Comparison is case-insensitive and matches on substrings, so
             * (for example) a filter value of "jose,maria" will match a
             * field containing either "jose" or "maria" anywhere within it.
             * Terms consisting only of whitespace are ignored. A filter
             * value that is empty (or contains only such terms) matches
             * everything.
             *
             * @param {!string} filterValue
             *     The raw, comma-separated filter value, as entered by the
             *     user.
             *
             * @param {string} fieldValue
             *     The value of the field being tested.
             *
             * @returns {!boolean}
             *     true if the field matches the filter, false otherwise.
             */
            var matchesStringFilter = function matchesStringFilter(filterValue, fieldValue) {

                var terms = filterValue.split(',')
                        .map((term) => term.trim().toLowerCase())
                        .filter((term) => !!term);

                if (!terms.length)
                    return true;

                var value = (fieldValue || '').toLowerCase();
                return terms.some((term) => value.indexOf(term) !== -1);

            };

            /**
             * Returns whether the given history entry wrapper matches the
             * column filters currently in effect. Used to further restrict
             * the already-retrieved history records without requiring
             * another request to the REST API.
             *
             * @param {!ConnectionHistoryEntryWrapper} historyEntryWrapper
             *     The wrapped history entry to test against the current
             *     column filters.
             *
             * @returns {Boolean}
             *     true if the given history entry matches all currently
             *     active column filters, false otherwise.
             */
            $scope.matchesColumnFilters = function matchesColumnFilters(historyEntryWrapper) {

                // The column filters have no effect at all while disabled
                if (!$scope.columnFiltersEnabled)
                    return true;

                var entry   = historyEntryWrapper.entry;
                var filters = $scope.columnFilters;

                if (!matchesStringFilter(filters.username, entry.username))
                    return false;

                if (!matchesStringFilter(filters.connectionName, entry.connectionName))
                    return false;

                if (!matchesStringFilter(filters.remoteHost, entry.remoteHost))
                    return false;

                if (filters.startDateFrom && (!entry.startDate || entry.startDate < filters.startDateFrom.getTime()))
                    return false;

                if (filters.startDateTo && (!entry.startDate || entry.startDate > filters.startDateTo.getTime()))
                    return false;

                if (filters.endDateFrom && (!entry.endDate || entry.endDate < filters.endDateFrom.getTime()))
                    return false;

                if (filters.endDateTo && (!entry.endDate || entry.endDate > filters.endDateTo.getTime()))
                    return false;

                return true;

            };

            /**
             * Returns whether any column filter is currently set to a
             * non-empty value. Used to determine whether the "Clear"
             * control should be shown, independently of whether the column
             * filters are currently enabled.
             *
             * @returns {Boolean}
             *     true if at least one column filter has a non-empty value,
             *     false otherwise.
             */
            $scope.hasColumnFilters = function hasColumnFilters() {
                var filters = $scope.columnFilters;
                return !!(
                    filters.username.trim()
                    || filters.connectionName.trim()
                    || filters.remoteHost.trim()
                    || filters.startDateFrom
                    || filters.startDateTo
                    || filters.endDateFrom
                    || filters.endDateTo
                );
            };

            /**
             * Clears all currently-active column filters, restoring the
             * unfiltered set of already-retrieved history records. Does not
             * affect whether the column filters are enabled/visible.
             */
            $scope.clearColumnFilters = function clearColumnFilters() {
                $scope.columnFilters.username       = '';
                $scope.columnFilters.connectionName = '';
                $scope.columnFilters.remoteHost     = '';
                $scope.columnFilters.startDateFrom  = null;
                $scope.columnFilters.startDateTo    = null;
                $scope.columnFilters.endDateFrom    = null;
                $scope.columnFilters.endDateTo      = null;
            };

            /**
             * Toggles whether the per-column filter row is visible and in
             * effect. Previously-entered filter values are preserved across
             * toggles, such that filtering can be quickly suspended and
             * resumed without having to re-enter them.
             */
            $scope.toggleColumnFilters = function toggleColumnFilters() {
                $scope.columnFiltersEnabled = !$scope.columnFiltersEnabled;
            };

            /**
             * Query the API for the connection record history, filtered by 
             * searchString, and ordered by order.
             */
            $scope.search = function search() {

                // Clear current results
                $scope.historyEntryWrappers = null;

                // Tokenize search string
                var tokens = FilterToken.tokenize($scope.searchString);

                // Transform tokens into list of required string contents
                var requiredContents = [];
                angular.forEach(tokens, function addRequiredContents(token) {

                    // Transform depending on token type
                    switch (token.type) {

                        // For string literals, use parsed token value
                        case 'LITERAL':
                            requiredContents.push(token.value);

                        // Ignore whitespace
                        case 'WHITESPACE':
                            break;

                        // For all other token types, use the relevant portion
                        // of the original search string
                        default:
                            requiredContents.push(token.consumed);

                    }

                });

                // Fetch history records
                historyService.getConnectionHistory(
                    $scope.dataSource,
                    requiredContents,
                    toAPISortPredicate($scope.order.predicate)
                )
                .then(function historyRetrieved(historyEntries) {

                    // Wrap all history entries for sake of display
                    $scope.historyEntryWrappers = [];
                    angular.forEach(historyEntries, function wrapHistoryEntry(historyEntry) {
                       $scope.historyEntryWrappers.push(new ConnectionHistoryEntryWrapper($scope.dataSource, historyEntry)); 
                    });

                }, requestService.DIE);

            };
            
            /**
             * The translation keys of all column headers used by both the
             * CSV and PDF exports, in display order.
             *
             * @type {!string[]}
             */
            const EXPORT_HEADER_KEYS = [
                'SETTINGS_CONNECTION_HISTORY.TABLE_HEADER_SESSION_USERNAME',
                'SETTINGS_CONNECTION_HISTORY.TABLE_HEADER_SESSION_STARTDATE',
                'SETTINGS_CONNECTION_HISTORY.TABLE_HEADER_SESSION_DURATION',
                'SETTINGS_CONNECTION_HISTORY.TABLE_HEADER_SESSION_ENDDATE',
                'SETTINGS_CONNECTION_HISTORY.TABLE_HEADER_SESSION_CONNECTION_NAME',
                'SETTINGS_CONNECTION_HISTORY.TABLE_HEADER_SESSION_REMOTEHOST'
            ];

            /**
             * Formats the given duration, in milliseconds, as a "HH:MM:SS"
             * string (hours are not capped at 24, as this represents an
             * elapsed duration rather than a time of day). Returns an empty
             * string if the duration is not a valid, non-negative number
             * (as is the case for sessions which are still active and thus
             * have no recorded end date).
             *
             * @param {number} milliseconds
             *     The duration to format, in milliseconds.
             *
             * @returns {!string}
             *     The given duration, formatted as "HH:MM:SS".
             */
            var formatDurationHHMMSS = function formatDurationHHMMSS(milliseconds) {

                if (!isFinite(milliseconds) || milliseconds < 0)
                    return '';

                var totalSeconds = Math.round(milliseconds / 1000);
                var hours   = Math.floor(totalSeconds / 3600);
                var minutes = Math.floor((totalSeconds % 3600) / 60);
                var seconds = totalSeconds % 60;

                var pad = (value) => (value < 10 ? '0' + value : '' + value);
                return pad(hours) + ':' + pad(minutes) + ':' + pad(seconds);

            };

            /**
             * Builds the header row and body rows describing the
             * currently-displayed (filtered and sorted) history search
             * results, for use by both the CSV and PDF exports.
             *
             * @param {!Object.<string, string>} translations
             *     The translated strings corresponding to EXPORT_HEADER_KEYS,
             *     as provided by $translate().
             *
             * @returns {!{header: !string[], rows: !Array.<!Array>}}
             *     The translated header row, along with one row per
             *     currently-visible history entry.
             */
            var buildExportTable = function buildExportTable(translations) {

                var header = EXPORT_HEADER_KEYS.map((key) => translations[key]);

                var rows = [];
                angular.forEach(
                    $filter('orderBy')(
                        $filter('filter')($scope.historyEntryWrappers, $scope.matchesColumnFilters),
                        $scope.order.predicate
                    ),
                    function pushRow(historyEntryWrapper) {
                        rows.push([
                            historyEntryWrapper.entry.username,
                            $filter('date')(historyEntryWrapper.entry.startDate, $scope.dateFormat),
                            formatDurationHHMMSS(historyEntryWrapper.duration),
                            historyEntryWrapper.entry.endDate ? $filter('date')(historyEntryWrapper.entry.endDate, $scope.dateFormat) : '',
                            historyEntryWrapper.entry.connectionName,
                            historyEntryWrapper.entry.remoteHost
                        ]);
                    }
                );

                return { header, rows };

            };

            /**
             * Initiates a download of a CSV version of the displayed history
             * search results.
             */
            $scope.downloadCSV = function downloadCSV() {

                $translate(EXPORT_HEADER_KEYS.concat(['SETTINGS_CONNECTION_HISTORY.FILENAME_HISTORY_CSV']))
                .then(function headerTranslated(translations) {

                    var table = buildExportTable(translations);

                    // Save the result
                    saveAs(
                        csvService.toBlob([table.header].concat(table.rows)),
                        translations['SETTINGS_CONNECTION_HISTORY.FILENAME_HISTORY_CSV']
                    );

                }, angular.noop);

            };

            /**
             * Initiates a download of a PDF version of the displayed history
             * search results, rendered as a table.
             */
            $scope.downloadPDF = function downloadPDF() {

                $translate(EXPORT_HEADER_KEYS.concat(['SETTINGS_CONNECTION_HISTORY.FILENAME_HISTORY_PDF']))
                .then(function headerTranslated(translations) {

                    var table = buildExportTable(translations);

                    var doc = new jsPDF({ orientation: 'landscape' });

                    autoTable(doc, {
                        head: [table.header],
                        body: table.rows,
                        styles: { fontSize: 8, cellPadding: 2 },
                        headStyles: { fillColor: [22, 86, 216] },
                        margin: { top: 12 }
                    });

                    doc.save(translations['SETTINGS_CONNECTION_HISTORY.FILENAME_HISTORY_PDF']);

                }, angular.noop);

            };

            // Initialize search results
            $scope.search();
            
        }]
    };
    
}]);
